/**
 * JWT Token Decoder Utility
 * 
 * Provides functions to decode and validate JWT tokens without verification.
 * For production, token verification should be done on the backend.
 */

/**
 * Decode JWT token payload
 * @param {string} token - JWT token string
 * @returns {object|null} Decoded token payload or null if invalid
 */
export const decodeJWT = (token) => {
  if (!token || typeof token !== 'string') {
    return null;
  }

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT token format');
      return null;
    }

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    const paddedBase64 = base64 + '='.repeat((4 - base64.length % 4) % 4);
    
    const jsonPayload = decodeURIComponent(
      atob(paddedBase64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

/**
 * Check if JWT token is expired
 * @param {string} token - JWT token string
 * @returns {boolean} True if token is expired or invalid
 */
export const isTokenExpired = (token) => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return true; // Consider invalid tokens as expired
  }
  
  // JWT exp is in seconds, convert to milliseconds
  const exp = decoded.exp * 1000;
  const now = Date.now();
  
  // Add 5 minute buffer to account for clock skew
  return now >= (exp - 5 * 60 * 1000);
};

/**
 * Get token expiration time
 * @param {string} token - JWT token string
 * @returns {Date|null} Expiration date or null if invalid
 */
export const getTokenExpiration = (token) => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return null;
  }
  
  return new Date(decoded.exp * 1000);
};

/**
 * Get token issued at time
 * @param {string} token - JWT token string
 * @returns {Date|null} Issued at date or null if invalid
 */
export const getTokenIssuedAt = (token) => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.iat) {
    return null;
  }
  
  return new Date(decoded.iat * 1000);
};

/**
 * Get remaining time until token expiration (in milliseconds)
 * @param {string} token - JWT token string
 * @returns {number|null} Remaining time in milliseconds or null if invalid/expired
 */
export const getTokenRemainingTime = (token) => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return null;
  }
  
  const exp = decoded.exp * 1000;
  const now = Date.now();
  const remaining = exp - now;
  
  return remaining > 0 ? remaining : 0;
};

/**
 * Extract user information from JWT token
 * @param {string} token - JWT token string
 * @returns {object|null} User info object or null if invalid
 */
export const extractUserInfo = (token) => {
  const decoded = decodeJWT(token);
  if (!decoded) {
    return null;
  }
  
  return {
    userId: decoded.userid || decoded.USER_ID || decoded.sub,
    username: decoded.username || decoded.preferred_username,
    email: decoded.email || decoded.username,
    tenantId: decoded.TENANT_ID || decoded.tenantId || decoded.tenant_id,
    roleId: decoded.roleid || decoded.role_id,
    roleCode: decoded.role_code || decoded.roleCode || decoded.ROLE_CODE, // Role code from JWT (e.g., "ADMIN", "AGENT")
    roles: decoded.roles || [],
    exp: decoded.exp,
    iat: decoded.iat,
  };
};

export default {
  decodeJWT,
  isTokenExpired,
  getTokenExpiration,
  getTokenIssuedAt,
  getTokenRemainingTime,
  extractUserInfo,
};

